import React, { Children, useEffect } from "react";
import { Organization } from "../../lib/types/types";
import { useFlag } from "../FeatureFlags/hooks";

interface OrganizationListProps {
  getItemProps: any;
  highlightedIndex: number;
  itemToString: any;
  organizations: Organization[];
  children: React.ReactNode;
  maxItemsToDisplay?: number; // eslint-disable-line
  // Next line is deprecated. Menu is always mounted now, up in TypeaheadComponent.
  getMenuProps?: any; // eslint-disable-line
}

const ROW_HEIGHT = 42;
const MAX_ITEMS = 26;

const OrganizationList = ({
  getItemProps,
  highlightedIndex,
  organizations,
  itemToString,
  children,
  maxItemsToDisplay = MAX_ITEMS,
}: OrganizationListProps) => {
  const childrenCount = Children.count(children);
  const fullHeight = (organizations.length + childrenCount) * ROW_HEIGHT;
  const itemRefs = {};
  const overlayEnabled = useFlag("org-search-overlay") === "overlay";

  useEffect(() => {
    const elem = document
      .getElementsByClassName("sid-organization-list__item--highlighted")
      .item(0);
    if (elem) {
      // elem.scrollIntoViewIfNeeded is nonstandard but has a better
      // experience, particularly on mobile browsers. So try it first.
      if ("scrollIntoViewIfNeeded" in elem && typeof elem.scrollIntoViewIfNeeded === "function") {
        elem.scrollIntoViewIfNeeded(true);
      } else {
        elem.scrollIntoView({ behavior: "instant", block: "center" });
      }
    }
  }, [highlightedIndex]);

  const item = (organization: Organization, index: number) => {
    const location = [organization.city, organization.state].filter(Boolean).join(", ");

    return (
      <div
        className={`sid-organization-list__item ${
          highlightedIndex === index ? "sid-organization-list__item--highlighted" : ""
        }`}
        key={index}
        role="option"
        aria-selected={highlightedIndex === index}
        {...getItemProps({
          index,
          item: organization,
        })}
        ref={itemRefs[highlightedIndex]}
      >
        {itemToString(organization)}
        {overlayEnabled && location && (
          <span className="sid-organization-list__location">{location}</span>
        )}
      </div>
    );
  };

  return (
    <div
      id="sid-org-list-menu"
      className="sid-organization-list__menu"
      tabIndex={-1}
      role="listbox"
    >
      <div style={{ height: Math.min(fullHeight, 280) }}>
        {organizations
          .filter((_, index) => index <= maxItemsToDisplay - 1)
          .map((organization, index) => item(organization, index))}
        {children}
      </div>
    </div>
  );
};

export const OrganizationListComponent = OrganizationList;
